<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { resolveCardStageName } from '../../../helpers/pipelineStageResolver';
import {
  formatInReportingTimezone,
  resolveReportingTimezone,
} from '../../../utils/localeFormatting';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const { t, locale } = useI18n();
const store = useStore();
const reportingTimezone = computed(() => resolveReportingTimezone(store));

// ── Helpers ────────────────────────────────────────────────────────────────────

const getUserLocale = () =>
  (locale.value || navigator.language || 'en-US').replace('_', '-');

const resolveStageName = stageId => {
  if (!stageId) return '-';
  return resolveCardStageName(
    { ...props.item, pipeline_stage: stageId },
    store.getters['pipelines/getPipelines'] || [],
    store.getters['pipelines/getSelectedPipeline']
  );
};

const formatAbsolute = date => {
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat(getUserLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: reportingTimezone.value,
    }).format(new Date(date));
  } catch {
    return '';
  }
};

const formatRelative = date => {
  if (!date) return '';
  try {
    const timestamp = new Date(date).getTime();
    if (!Number.isFinite(timestamp)) return '';
    const diffMinutes = Math.round((timestamp - Date.now()) / 60000);
    const formatter = new Intl.RelativeTimeFormat(getUserLocale(), {
      numeric: 'auto',
    });
    if (Math.abs(diffMinutes) < 60) {
      return formatter.format(diffMinutes, 'minute');
    }
    const diffHours = Math.round(diffMinutes / 60);
    if (Math.abs(diffHours) < 24) return formatter.format(diffHours, 'hour');
    return formatter.format(Math.round(diffHours / 24), 'day');
  } catch {
    return '';
  }
};

const getDayKey = date => {
  if (!date) return 'unknown';
  return (
    formatInReportingTimezone(date, 'yyyy-MM-dd', reportingTimezone.value) ||
    'unknown'
  );
};

const previousCalendarDayKey = dayKey => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayKey);
  if (!match) return 'unknown';
  const [, year, month, day] = match;
  const previousDay = new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day) - 1)
  );
  return previousDay.toISOString().slice(0, 10);
};

const getDayLabel = date => {
  if (!date) return '';
  try {
    const d = new Date(date);
    const todayKey = getDayKey(new Date());
    const dayKey = getDayKey(d);

    if (dayKey === todayKey) {
      return t('PIPELINE.ACTIVITY.GROUP_TODAY');
    }
    if (dayKey === previousCalendarDayKey(todayKey)) {
      return t('PIPELINE.ACTIVITY.GROUP_YESTERDAY');
    }

    return new Intl.DateTimeFormat(getUserLocale(), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: reportingTimezone.value,
    }).format(d);
  } catch {
    return '';
  }
};

// ── Normalise raw activities from item.item_details.history ───────────────────

const FIELD_LABEL_KEY_MAP = {
  title: 'TITLE',
  priority: 'PRIORITY',
  value: 'VALUE',
  expected_revenue: 'EXPECTED_REVENUE',
};

const normaliseActivity = (raw, index) => {
  const ts = raw.timestamp || raw.created_at || null;
  const user =
    raw.user_name ||
    raw.user?.name ||
    raw.details?.user?.name ||
    t('PIPELINE.SYSTEM');
  const activityType = raw.type || raw.activity_type || 'unknown';

  let type = 'other';
  let icon = 'info';
  let description = '';

  switch (activityType) {
    case 'stage_changed':
      type = 'stage_changed';
      icon = 'arrow-right';
      description = t('PIPELINE.ACTIVITY.STAGE_CHANGED', {
        from: resolveStageName(raw.details?.old_stage),
        to: resolveStageName(raw.details?.new_stage),
      });
      break;

    case 'owner_changed': {
      type = 'owner_changed';
      icon = 'person-assign';
      const newOwner = raw.details?.new_owner || '-';
      description = t('PIPELINE.ACTIVITY.OWNER_CHANGED', { owner: newOwner });
      break;
    }

    case 'status_changed':
      if (raw.details?.new_status === 'won') {
        type = 'won';
        icon = 'checkmark-circle';
        description = t('PIPELINE.ACTIVITY.MARKED_WON');
      } else if (raw.details?.new_status === 'lost') {
        type = 'lost';
        icon = 'dismiss-circle';
        description = t('PIPELINE.ACTIVITY.MARKED_LOST');
      } else {
        type = 'other';
        icon = 'status';
        description = `${raw.details?.old_status || '?'} → ${raw.details?.new_status || '?'}`;
      }
      break;

    case 'title_changed':
    case 'priority_changed':
    case 'value_changed':
    case 'agent_changed': {
      type = 'field_edited';
      icon = 'edit';
      const fieldKey = activityType.replace('_changed', '');
      const labelKey = FIELD_LABEL_KEY_MAP[fieldKey];
      const fieldLabel = labelKey
        ? t(`PIPELINE.ACTIVITY.FIELDS.${labelKey}`)
        : fieldKey;
      description = t('PIPELINE.ACTIVITY.FIELD_EDITED', { field: fieldLabel });
      break;
    }

    case 'note_added':
      type = 'field_edited';
      icon = 'document';
      description = t('PIPELINE.ACTIVITY.NOTE_ADDED', 'Nota adicionada');
      break;

    case 'note_edited':
      type = 'field_edited';
      icon = 'edit';
      description = t('PIPELINE.ACTIVITY.NOTE_EDITED', 'Nota editada');
      break;

    case 'note_deleted':
      type = 'field_edited';
      icon = 'delete';
      description = t('PIPELINE.ACTIVITY.NOTE_DELETED', 'Nota removida');
      break;

    case 'attachment_uploaded':
      type = 'field_edited';
      icon = 'attach';
      description = t('PIPELINE.ACTIVITY.ATTACHMENT_UPLOADED', 'Anexo enviado');
      break;

    case 'inbox_transferred':
      type = 'inbox_transferred';
      icon = 'arrow-swap';
      description = t('PIPELINE.ACTIVITY.INBOX_TRANSFERRED', {
        from: raw.details?.from_inbox || '?',
        to: raw.details?.to_inbox || '?',
      });
      break;

    // FU-07: regra de follow-up que casou com a mudança de etapa mas não armou.
    case 'follow_up_rule_not_fired':
      type = 'follow_up_rule_not_fired';
      icon = 'warning';
      description = t('PIPELINE.ACTIVITY.FOLLOW_UP_RULE_NOT_FIRED', {
        rules: (raw.details?.rules || []).join(', ') || '?',
        reason: t(
          `PIPELINE.ACTIVITY.FOLLOW_UP_RULE_NOT_FIRED_REASON.${String(
            raw.details?.reason || 'UNKNOWN'
          ).toUpperCase()}`
        ),
      });
      break;

    default:
      type = 'other';
      icon = 'info';
      description = activityType;
      break;
  }

  return {
    id: raw.id || `act-${index}`,
    type,
    icon,
    description,
    author: user,
    timestamp: ts,
    raw,
  };
};

// ── Filter ─────────────────────────────────────────────────────────────────────

const activeFilter = ref('all');

const filterOptions = [
  { id: 'all', label: computed(() => t('PIPELINE.ACTIVITY.FILTER_ALL')) },
  {
    id: 'stage_changed',
    label: computed(() => t('PIPELINE.ACTIVITY.FILTER_STAGES')),
  },
  {
    id: 'owner_changed',
    label: computed(() => t('PIPELINE.ACTIVITY.FILTER_OWNER')),
  },
  {
    id: 'win_loss',
    label: computed(() => t('PIPELINE.ACTIVITY.FILTER_WIN_LOSS')),
  },
  {
    id: 'field_edited',
    label: computed(() => t('PIPELINE.ACTIVITY.FILTER_EDITS')),
  },
];

// ── Build unified timeline ─────────────────────────────────────────────────────

const allActivities = computed(() => {
  // Merge all sources rather than fall through with `||` — a card may have
  // BOTH item_details.history (legacy) AND item_details.activities (current),
  // and `truthy || other` would silently drop the second array.
  const raw = [
    ...(Array.isArray(props.item.item_details?.history)
      ? props.item.item_details.history
      : []),
    ...(Array.isArray(props.item.item_details?.activities)
      ? props.item.item_details.activities
      : []),
    ...(Array.isArray(props.item.activities) ? props.item.activities : []),
  ];

  // Deduplicate by id when present (avoids showing the same entry twice if
  // a sync between history and activities accidentally duplicated rows).
  const seen = new Set();
  const unique = raw.filter(act => {
    if (!act?.id) return true;
    if (seen.has(act.id)) return false;
    seen.add(act.id);
    return true;
  });

  return unique
    .map((act, i) => normaliseActivity(act, i))
    .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
});

const filteredActivities = computed(() => {
  if (activeFilter.value === 'all') return allActivities.value;
  if (activeFilter.value === 'win_loss') {
    return allActivities.value.filter(
      a => a.type === 'won' || a.type === 'lost'
    );
  }
  return allActivities.value.filter(a => a.type === activeFilter.value);
});

const groupedActivities = computed(() => {
  const groups = [];
  const seen = new Map();

  filteredActivities.value.forEach(act => {
    const key = getDayKey(act.timestamp);
    if (!seen.has(key)) {
      seen.set(key, groups.length);
      groups.push({
        day: key,
        dayLabel: getDayLabel(act.timestamp),
        items: [],
      });
    }
    groups[seen.get(key)].items.push(act);
  });

  return groups;
});

// ── Icon color by type ─────────────────────────────────────────────────────────

const iconColorClass = type => {
  switch (type) {
    case 'stage_changed':
      return 'text-n-brand';
    case 'owner_changed':
      return 'text-amber-11';
    case 'won':
      return 'text-emerald-11';
    case 'lost':
      return 'text-n-ruby-11';
    case 'field_edited':
      return 'text-n-slate-11';
    case 'inbox_transferred':
      return 'text-n-brand';
    case 'follow_up_rule_not_fired':
      return 'text-n-amber-11';
    default:
      return 'text-n-slate-10';
  }
};
</script>

<template>
  <div class="space-y-4">
    <!-- Filters -->
    <div class="flex gap-2 flex-wrap">
      <button
        v-for="filter in filterOptions"
        :key="filter.id"
        type="button"
        class="min-h-11 px-3 py-2 text-xs rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        :aria-pressed="activeFilter === filter.id"
        :class="
          activeFilter === filter.id
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
        "
        @click="activeFilter = filter.id"
      >
        {{ filter.label }}
      </button>
    </div>

    <!-- Empty state -->
    <div
      v-if="groupedActivities.length === 0"
      class="flex flex-col items-center justify-center py-12 text-center"
    >
      <fluent-icon
        icon="arrow-clockwise"
        size="32"
        class="text-n-slate-9 mb-2"
      />
      <p class="text-sm text-n-slate-10">
        {{ t('PIPELINE.ACTIVITY.EMPTY_STATE') }}
      </p>
    </div>

    <!-- Day groups -->
    <div v-for="group in groupedActivities" :key="group.day" class="space-y-2">
      <h4 class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">
        {{ group.dayLabel }}
      </h4>

      <div class="border-l-2 border-n-weak pl-4 space-y-3">
        <div
          v-for="act in group.items"
          :key="act.id"
          class="relative flex items-start gap-3"
        >
          <!-- Timeline dot -->
          <span
            class="absolute -left-[1.375rem] top-1 h-3 w-3 rounded-full border-2 border-n-bg-3 bg-n-solid-3 flex-shrink-0"
            aria-hidden="true"
          />

          <!-- Icon -->
          <fluent-icon
            :icon="act.icon"
            size="14"
            class="flex-shrink-0 mt-0.5"
            :class="iconColorClass(act.type)"
          />

          <!-- Content -->
          <div class="flex-1 min-w-0">
            <p class="text-sm text-n-slate-12 leading-snug">
              {{ act.description }}
            </p>
            <p class="text-xs text-n-slate-10 mt-0.5">
              <span>{{ act.author }}</span>
              <span
                v-if="act.timestamp"
                :title="formatAbsolute(act.timestamp)"
                class="ml-1"
                :aria-label="formatAbsolute(act.timestamp)"
              >
                {{ formatRelative(act.timestamp) }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
